import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild, Renderer2, OnDestroy, DOCUMENT } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { filter, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PSUser } from './models/ps-user.model';
import { AuthService } from './services/auth.service/auth.service';
import { LoggingService } from './services/logging-service/logging-service.service';
import { UserService } from './services/uppy/user.service';
import { TrainingProgramApplicationUtils } from './utils/training-program-application.util';


declare function navigate(url): string;
declare function getCurUrl(): string;
declare const gtag: (...args: any[]) => void;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  year: number = moment().year();
  domain: string;
  title: string = '';
  currentLang: string
  userName: string = '';
  menuOpen: boolean = false;
  showSidebar: boolean = false;
  showGoToTop: boolean = false;
  showSwitchLink: boolean = false;
  showBack = false;
  user: PSUser;
  WORKER = 'WORKER';
  EMPLOYER = 'EMPLOYER';
  site: 'public' | 'tp' | 'learner' | 'employer' | 'login' | 'registration' = 'public';
  @ViewChild('headerContainerSecondRow') headerContainerSecondRow: ElementRef;
  @ViewChild('headerContainerFirstRow') headerContainerFirstRow: ElementRef;
  @ViewChild('ontgov') ontgov: ElementRef;
  @ViewChild('mainContentContainer') mainContentContainer: ElementRef;

  hasLearnerRole: boolean = false;
  hasTrainingProviderRole: boolean = false;
  hasEmployerRole: boolean = false;
  private langChangeSubscription?: Subscription;


  constructor(
    public router: Router,
    public titleService: Title,
    public activatedRoute: ActivatedRoute,
    public translateService: TranslateService,
    public userService: UserService,
    @Inject('AuthService') public authService: AuthService,
    private myLoggingService: LoggingService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.domain = environment.apiBaseUrl.replace('/api/', '');
    //Add Google Analytics Script Dynamically
    this.addGAScript();
    this.currentLang = translateService.currentLang;
    translateService.onLangChange.subscribe(lan => this.currentLang = lan.lang);
  }

  addGAScript() {
    let gtagScript: HTMLScriptElement = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + environment.googleAnalyticsTrackingCode;
    document.head.prepend(gtagScript);
    //Disable automatic page view hit to fix duplicate page view count
    gtag('config', environment.googleAnalyticsTrackingCode, { send_page_view: false });
  }

  ngOnInit(): void {
    this.langChangeSubscription = this.translateService.onLangChange.subscribe((event) => {
      this.updateLangAttribute(event.lang);
    });
    this.updateLangAttribute(this.translateService.currentLang);
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.getChild(this.activatedRoute).data.subscribe(data => {
        if (data && data.title) this.translateService.get(data.title).subscribe((result: string) => this.titleService.setTitle(result));
      })
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.checkIfLoggedIn();
        //Code to Track Page View
        gtag('event', 'page_view', { page_path: event.urlAfterRedirects });
        this.showSidebar = false;
        let url = this.router.url.toLowerCase();
        if (url.startsWith('/training-provider') || url.startsWith('/fr/training-provider')) {
          this.site = 'tp';
          this.showSidebar = url.startsWith('/training-provider/my-training-programs')
            || url.startsWith('/fr/training-provider/my-training-programs')
            || url.startsWith('/training-provider/training-schedules')
            || url.startsWith('/fr/training-provider/training-schedules')
            || url.startsWith('/training-provider/teams')
            || url.startsWith('/fr/training-provider/teams')
            || url.startsWith('/training-provider/my-notifications')
            || url.startsWith('/fr/training-provider/my-notifications')
            || url.startsWith('/training-provider/my-learners')
            || url.startsWith('/fr/training-provider/my-learners')
            || url.startsWith('/training-provider/contact-us')
            || url.startsWith('/fr/training-provider/contact-us')
            || url.startsWith('/training-provider/faqs')
            || url.startsWith('/fr/training-provider/faqs')
            || url.startsWith('/training-provider/reporting')
            || url.startsWith('/fr/training-provider/reporting')
            || url.startsWith('/training-provider/account-settings')
            || url.startsWith('/fr/training-provider/account-settings');
        } else if (url.startsWith('/worker') || url.startsWith('/fr/worker')) {
          this.site = 'learner';
          this.showSidebar = url.startsWith('/worker/certifications')
            || url.startsWith('/fr/worker/certifications')
            || url.startsWith('/worker/profile-review')
            || url.startsWith('/fr/worker/profile-review')
            || url.startsWith('/worker/training-schedules')
            || url.startsWith('/fr/worker/training-schedules')
            || url.startsWith('/worker/notifications')
            || url.startsWith('/fr/worker/notifications')
            || url.startsWith('/worker/contact-us')
            || url.startsWith('/fr/worker/contact-us')
            || url.startsWith('/worker/faq')
            || url.startsWith('/fr/worker/faq');
          this.showBack = url.indexOf('/worker/share-records-preview/') >= 0
            || url.indexOf('create-exemption-request') >= 0
            || url.indexOf('view-exemption-request') >= 0
            || url.indexOf('enrolment-status-detail') >= 0;
        } else if (url.startsWith('/employer') || url.startsWith('/fr/employer')) {
          this.site = 'employer';
          this.showSidebar = url.startsWith('/employer')
            || url.startsWith('/fr/employer')
            // || url.startsWith('/employer/faq')
            // || url.startsWith('/fr/employer/faq');
            || url.startsWith('/employer/my-worker-records')
            || url.startsWith('/fr/employer/my-worker-records')
            || url.startsWith('/employer/contact-us')
            || url.startsWith('/fr/employer/contact-us')
          // this.showBack = url.indexOf('/employer/share-records-preview/') >= 0
          //   || url.indexOf('create-exemption-request') >= 0
          //   || url.indexOf('view-exemption-request') >= 0
          //   || url.indexOf('enrolment-status-detail') >= 0;
        } 
        // else if (url.startsWith('/prevention-cms') || url.startsWith('/fr/prevention-cms')) {
        //   this.site = 'login';
        // }
        else if (url.startsWith('/registration/') || url.startsWith('/fr/registration/')
        ) {
          this.site = 'registration';
        }
        else {
          this.site = 'public';
        }
      } else if (event instanceof NavigationStart) {
        this.myLoggingService.startNavigationEvent(event.url);
      }
    });
  }

  ngOnDestroy() {
    this.langChangeSubscription!.unsubscribe();
  }

  private updateLangAttribute(lang: string) {
    this.renderer.setAttribute(this.document.documentElement, 'lang', lang);
  }

  getChild(activatedRoute: ActivatedRoute) {
    return activatedRoute.firstChild ? this.getChild(activatedRoute.firstChild) : activatedRoute;
  }

  checkIfLoggedIn() {
    try {
      this.userService.GetCurrentUser().subscribe(user => {
        this.user = user;
        sessionStorage.setItem('loggedIn', 'true')
        this.userService.currentUser = user;
        this.userName = (user.FirstName + ' ' + user.LastName)
        if (user.TrainingProviderName) this.title = user.TrainingProviderName;
        this.authService.setIsAuthenticated(true)

        const userRoles = user.Roles?.map((role) => (role.PersonRoleId ?? role.RoleName!).toUpperCase());
        if (userRoles || user.Employer) {
          const distinctUserRoles = [...new Set(userRoles)];
          if (user.Employer) distinctUserRoles.push(this.EMPLOYER);

          const tpRoles = [
            TrainingProgramApplicationUtils.AdministratorId.toLowerCase(),
            TrainingProgramApplicationUtils.AlternateSigningAuthorityId.toLowerCase(),
            TrainingProgramApplicationUtils.DesignatedSigningAuthorityId.toLowerCase(),
            TrainingProgramApplicationUtils.AffiliateAdministratorId.toLowerCase(),
            TrainingProgramApplicationUtils.InstructorId.toLowerCase(),
            TrainingProgramApplicationUtils.AffiliateInstructorId.toLowerCase()
          ];

          this.hasLearnerRole = distinctUserRoles.includes(this.WORKER);
          this.hasTrainingProviderRole = distinctUserRoles.some(role => tpRoles.includes(role.toLowerCase()));
          this.hasEmployerRole = distinctUserRoles.includes(this.EMPLOYER);
          this.showSwitchLink = (this.hasLearnerRole || this.hasTrainingProviderRole || this.hasEmployerRole) && distinctUserRoles.length > 0 ? true : false;
        }

        //Chain another api call to trigger the interceptor that handles
        this.userService.GetCurrentUser().subscribe()
      }, err => {
        sessionStorage.removeItem('loggedIn')
        this.authService.setIsAuthenticated(false)
      })
    } catch (e) {
      sessionStorage.removeItem('loggedIn')
      this.authService.setIsAuthenticated(false)
    }
  }

  goTrainingPortal() {
    let url = 'training-provider/register-as-training-provider';
    if (this.user.Roles == null) return;
    if (this.user.Roles.filter(x => x.PersonRoleId == TrainingProgramApplicationUtils.AdministratorId.toLocaleLowerCase() || x.PersonRoleId == TrainingProgramApplicationUtils.AlternateSigningAuthorityId.toLocaleLowerCase()
      || x.PersonRoleId == TrainingProgramApplicationUtils.DesignatedSigningAuthorityId.toLocaleLowerCase()).length > 0)
      url = '/training-provider/my-training-programs';
    else if (this.user.Roles.filter(x => x.PersonRoleId == TrainingProgramApplicationUtils.AffiliateAdministratorId.toLocaleLowerCase() || x.PersonRoleId == TrainingProgramApplicationUtils.InstructorId.toLocaleLowerCase() || x.PersonRoleId == TrainingProgramApplicationUtils.AffiliateInstructorId.toLocaleLowerCase()).length > 0)
      url = '/training-provider/training-schedules';

    this.router.navigate([url]);
  }

  goEmployerPortal() {
    let url = 'employer/register-employer';
    if (this.user.Employer) {
      url = '/employer/my-worker-records';
    }
          
    this.router.navigate([url]);
  }

  signOut() {
    sessionStorage.clear();
    localStorage.clear();
    this.userService.gotoLogout();
  }

  changeLan() {
    let url = this.router.url;
    let root = getCurUrl().replace(url, '');
    navigate(root + ((this.currentLang == 'en') ? '/fr' + url : url.substring(3)));
  }

  @HostListener('window:scroll')
  OnWindowScroll() { this.showGoToTop = window.scrollY > 0; }
  ScrollToTop() { 
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    this.ontgov.nativeElement.focus()
  }
}
